import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SiteHeader from "@/components/SiteHeader";
import MyMatches from "@/components/MyMatches";
import TicketsFighter from "@/components/TicketsFighter";

const STATUS = {
  pending_review: { label: "Under Committee Review", cls: "text-yellow-500 border-yellow-800/60 bg-yellow-950/30" },
  approved: { label: "Sanctioned Fighter — Approved", cls: "text-green-500 border-green-800/60 bg-green-950/30" },
  rejected: { label: "Not Sanctioned", cls: "text-red-500 border-red-800/60 bg-red-950/30" },
};

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/fighter/application")
      .then((r) => setApplication(r.data))
      .catch((e) => {
        if (e?.response?.status !== 404) toast.error(formatApiError(e));
      })
      .finally(() => setLoading(false));
  }, [user]);

  const st = application ? STATUS[application.status] || STATUS.pending_review : null;

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="dashboard-page">
      <SiteHeader />
      <div className="max-w-4xl mx-auto px-4 sm:px-8 pt-32 pb-24">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-10">
          <div>
            <p className="font-mono2 text-[10px] tracking-[0.4em] text-[#E10600] uppercase mb-3 flex items-center gap-4">
              <span className="h-px w-10 bg-[#E10600]" /> Locker Room
            </p>
            <h1 className="font-display text-5xl sm:text-6xl uppercase leading-[0.9] text-white" data-testid="dashboard-fighter-name">
              {user?.name || "Fighter"}
            </h1>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="dashboard-logout-button"
            className="font-cond font-semibold tracking-[0.2em] text-xs border border-zinc-700 hover:border-[#E10600] hover:text-[#E10600] text-zinc-300 px-5 py-2.5 transition-colors duration-300"
          >
            LOGOUT
          </button>
        </div>

        {loading ? (
          <p className="font-mono2 text-xs tracking-[0.3em] text-zinc-500 uppercase" data-testid="dashboard-loading">Loading your file...</p>
        ) : application ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-6">
            <div className={`border p-6 sm:p-8 ${st.cls}`} data-testid="application-status-card">
              <p className="font-mono2 text-[10px] tracking-[0.35em] uppercase opacity-80 mb-2">Application Status</p>
              <p className="font-display text-4xl sm:text-5xl uppercase" data-testid="application-status-label">{st.label}</p>
              {application.status === "approved" && (
                <p className="text-sm opacity-90 mt-3" data-testid="approved-note">
                  You are a sanctioned MPB fighter. Your scheduled bouts and results appear below.
                </p>
              )}
              {application.status === "rejected" && (
                <p className="text-sm opacity-90 mt-3" data-testid="rejected-note">
                  Your application was not sanctioned this season. Raise a support ticket below for details.
                </p>
              )}
            </div>

            <MyMatches />

            <div className="border border-zinc-800 bg-[#111111] p-6 sm:p-8" data-testid="application-details-card">
              <p className="font-mono2 text-[10px] tracking-[0.35em] text-zinc-500 uppercase mb-6">Fighter File</p>
              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                {[
                  ["Age", application.age],
                  ["Division", application.weight_division],
                  ["Level", application.experience_level === "professional" ? "Professional" : "Fresher"],
                  ["Pro Record (W-L-D)", application.pro_record],
                  ["Email", application.email],
                  ["Phone", application.phone],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="font-mono2 text-[9px] tracking-[0.3em] text-zinc-500 uppercase mb-1">{k}</dt>
                    <dd className="font-cond font-semibold text-lg text-white break-words" data-testid={`fighter-file-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <TicketsFighter />
          </motion.div>
        ) : (
          <div className="border border-zinc-800 bg-[#111111] p-8 text-center" data-testid="no-application-card">
            <p className="text-zinc-400 mb-6">No application on file yet.</p>
            <Link to="/register" data-testid="dashboard-register-link" className="font-cond font-semibold tracking-[0.25em] bg-[#E10600] hover:bg-[#C00500] text-white px-8 py-3.5 transition-colors duration-300">
              REGISTER NOW
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
